import _toConsumableArray from "/vercel/path0/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
globalThis.jotaiAtomCache = globalThis.jotaiAtomCache || {
  cache: new Map(),
  get: function get(name, inst) {
    if (this.cache.has(name)) {
      return this.cache.get(name);
    }

    this.cache.set(name, inst);
    return inst;
  }
};
import _styled from "styled-components";
import { Float } from '@headlessui-float/react';
import { Popover as DefaultPopover, Transition } from '@headlessui/react';
import React, { useState, useRef, useEffect } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export var Trigger;

(function (Trigger) {
  Trigger["CLICK"] = "click";
  Trigger["HOVER"] = "hover";
})(Trigger || (Trigger = {}));

/**
 * A customizable popover component that can be triggered by click or hover.
 * @param children - The content of the popover, which should include a Popover.Button and a Popover.Panel.
 * @param trigger - The trigger type for the popover. Defaults to Trigger.CLICK.
 * @param buttonTwStyle - The Tailwind CSS styles to apply to the Popover.Button component.
 * @param panelTwStyle - The Tailwind CSS styles to apply to the Popover.Panel component.
 * @param haveDefaultButtonStyle - Whether to apply the default button style to the Popover.Button component. Defaults to true.
 * @example
    import { Popover, Trigger } from './popover';

    const ExamplePopover = () => {
      return (
        <Popover trigger={Trigger.HOVER}>
          <Popover.Button>Hover me</Popover.Button>
          <Popover.Panel>
            <div tw="p-4 bg-white shadow-md rounded-md">
              <p tw="text-gray-800">Hello, world!</p>
            </div>
          </Popover.Panel>
        </Popover>
      );
    };
 */
export var Popover = function Popover(_ref) {
  var children = _ref.children,
      _ref$trigger = _ref.trigger,
      trigger = _ref$trigger === void 0 ? Trigger.CLICK : _ref$trigger,
      buttonTwStyle = _ref.buttonTwStyle,
      panelTwStyle = _ref.panelTwStyle,
      _ref$haveDefaultButto = _ref.haveDefaultButtonStyle,
      haveDefaultButtonStyle = _ref$haveDefaultButto === void 0 ? true : _ref$haveDefaultButto,
      _ref$fullWidth = _ref.fullWidth,
      fullWidth = _ref$fullWidth === void 0 ? false : _ref$fullWidth,
      popoverTwStyle = _ref.popoverTwStyle;

  var _useState = useState(false),
      show = _useState[0],
      setShow = _useState[1];

  var buttonRef = useRef(null);
  var panelRef = useRef(null);
  var button = undefined;
  var panel = undefined;

  var handleClickOutside = function handleClickOutside(event) {
    if (buttonRef.current && !buttonRef.current.contains(event.target) && panelRef.current && !panelRef.current.contains(event.target)) {
      event.stopPropagation();
      setShow(false);
    }
  };

  var handleMouseEnter = function handleMouseEnter() {
    if (trigger === Trigger.HOVER) {
      var _buttonRef$current;

      setShow(true);
      buttonRef === null || buttonRef === void 0 ? void 0 : (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.click();
    }
  };

  var handleMouseLeave = function handleMouseLeave() {
    if (trigger === Trigger.HOVER) {
      setShow(false);
    }
  };

  useEffect(function () {
    var _document;

    var handleOutsideClick = function handleOutsideClick(event) {
      handleClickOutside(event);
    };

    (_document = document) === null || _document === void 0 ? void 0 : _document.addEventListener('mousedown', handleOutsideClick);
    return function () {
      var _document2;

      (_document2 = document) === null || _document2 === void 0 ? void 0 : _document2.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);
  React.Children.forEach(children, function (child) {
    if ( /*#__PURE__*/React.isValidElement(child)) {
      if (child.type === Popover.Button) {
        button = child;
      } else if (child.type === Popover.Panel) {
        panel = child;
      }
    }
  });

  var handleClick = function handleClick(open) {
    setShow(!open);
  };

  return /*#__PURE__*/_jsx(StyledPopover, {
    $fullWidth: fullWidth,
    $popoverTwStyle: popoverTwStyle,
    children: function children(_ref2) {
      var open = _ref2.open;
      return /*#__PURE__*/_jsx(StyledPopoverEntry, {
        $fullWidth: fullWidth,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: /*#__PURE__*/_jsxs(Float, {
          placement: "bottom-start",
          flip: true,
          zIndex: 900,
          transform: true,
          portal: true,
          offset: 6,
          show: show,
          children: [/*#__PURE__*/_jsx(StyledPopoverButton, {
            onClick: function onClick() {
              trigger === Trigger.CLICK && handleClick(open);
            },
            $buttonTwStyle: buttonTwStyle,
            $fullWidth: fullWidth,
            ref: buttonRef,
            $haveDefaultButtonStyle: haveDefaultButtonStyle,
            disabled: trigger === Trigger.HOVER,
            children: button
          }), /*#__PURE__*/_jsx(Transition, {
            as: 'div',
            show: show,
            enter: "transition ease-out duration-200",
            enterFrom: "opacity-0 translate-y-1",
            enterTo: "opacity-100 translate-y-0",
            leave: "transition ease-in duration-150",
            leaveFrom: "opacity-100 translate-y-0",
            leaveTo: "opacity-0 translate-y-1",
            children: /*#__PURE__*/_jsx(StyledPopoverPanel, {
              panelTwStyle: panelTwStyle,
              children: panel
            })
          })]
        })
      });
    }
  });
};

var StyledPopover = /*#__PURE__*/_styled(DefaultPopover).withConfig({
  displayName: "custom-popover__StyledPopover",
  componentId: "sc-dos1c0-0"
})(function (_ref3) {
  var twStyle = _ref3.$popoverTwStyle,
      $fullWidth = _ref3.$fullWidth;
  return [$fullWidth && {
    "width": "100%"
  }].concat(_toConsumableArray(twStyle && Array.isArray(twStyle) ? twStyle : []), [twStyle && !Array.isArray(twStyle) ? twStyle : {}]);
});

var StyledPopoverEntry = /*#__PURE__*/_styled.div.withConfig({
  displayName: "custom-popover__StyledPopoverEntry",
  componentId: "sc-dos1c0-1"
})(function (_ref4) {
  var $fullWidth = _ref4.$fullWidth;
  return [{
    "width": "fit-content"
  }, $fullWidth && {
    "width": "100%"
  }];
});

var StyledPopoverButton = /*#__PURE__*/_styled(DefaultPopover.Button).withConfig({
  displayName: "custom-popover__StyledPopoverButton",
  componentId: "sc-dos1c0-2"
})(function (_ref5) {
  var twstyle = _ref5.$buttonTwStyle,
      $haveDefaultButtonStyle = _ref5.$haveDefaultButtonStyle,
      $fullWidth = _ref5.$fullWidth;
  return [].concat(_toConsumableArray($haveDefaultButtonStyle ? [{
    "position": "relative",
    "cursor": "pointer",
    "borderRadius": "0.375rem",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(249 250 251 / var(--tw-bg-opacity))",
    "padding": "0.5rem",
    "fontSize": "1rem",
    "lineHeight": "1.5rem",
    "--tw-text-opacity": "1",
    "color": "rgb(28 19 39 / var(--tw-text-opacity))",
    ":hover": {
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(243 244 246 / var(--tw-bg-opacity))",
      "--tw-text-opacity": "1",
      "color": "rgb(138 112 184 / var(--tw-text-opacity))"
    },
    ":focus": {
      "outline": "2px solid transparent",
      "outlineOffset": "2px",
      "--tw-ring-offset-shadow": "var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
      "--tw-ring-shadow": "var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
      "boxShadow": "var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)",
      "--tw-ring-opacity": "1",
      "--tw-ring-color": "rgb(255 255 255 / var(--tw-ring-opacity))",
      "--tw-ring-offset-width": "2px",
      "--tw-ring-offset-color": "#e2d8f3"
    },
    "@media (min-width: 768px)": {
      "width": "50%",
      "overflow": "hidden",
      "textOverflow": "ellipsis",
      "whiteSpace": "nowrap",
      "backgroundColor": "transparent",
      "fontSize": "0.875rem",
      "lineHeight": "1.25rem"
    },
    "@media (min-width: 1024px)": {
      "width": "fit-content",
      "fontSize": "1rem",
      "lineHeight": "1.5rem"
    }
  }] : []), [$fullWidth && {
    "width": "100%"
  }, !$haveDefaultButtonStyle && {
    ":focus-visible": {
      "borderStyle": "none",
      "--tw-shadow": "0 0 #0000",
      "--tw-shadow-colored": "0 0 #0000",
      "boxShadow": "var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)",
      "outline": "2px solid transparent",
      "outlineOffset": "2px",
      "--tw-ring-offset-shadow": "var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
      "--tw-ring-shadow": "var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color)"
    }
  }], _toConsumableArray(twstyle && Array.isArray(twstyle) ? twstyle : []), [twstyle && !Array.isArray(twstyle) ? twstyle : {}]);
});

var StyledPopoverPanel = /*#__PURE__*/_styled(DefaultPopover.Panel).withConfig({
  displayName: "custom-popover__StyledPopoverPanel",
  componentId: "sc-dos1c0-3"
})(function (_ref6) {
  var twStyle = _ref6.panelTwStyle;
  return [{
    "display": "flex !important",
    "width": "fit-content",
    "> :not([hidden]) ~ :not([hidden])": {
      "--tw-divide-y-reverse": "0",
      "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
      "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))",
      "--tw-divide-opacity": "1",
      "borderColor": "rgb(243 244 246 / var(--tw-divide-opacity))"
    },
    "borderRadius": "0.375rem",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
    "padding": "1.5rem",
    "--tw-shadow": "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    "--tw-shadow-colored": "0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color)",
    "boxShadow": "var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)",
    "--tw-ring-offset-shadow": "var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
    "--tw-ring-shadow": "var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
    "--tw-ring-opacity": "0.05",
    "--tw-ring-color": "rgb(28 19 39 / var(--tw-ring-opacity))",
    ":focus": {
      "outline": "2px solid transparent",
      "outlineOffset": "2px"
    }
  }].concat(_toConsumableArray(twStyle && Array.isArray(twStyle) ? twStyle : []), [twStyle && !Array.isArray(twStyle) ? twStyle : {}]);
});

Popover.Button = function (_ref7) {
  var children = _ref7.children;
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};

Popover.Panel = function (_ref8) {
  var children = _ref8.children;
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};